/**
 * 统一邮件模型 - 用于邮件列表和详情的数据流转
 */

// ==================== 基础类型定义 ====================

/** 消息类型 */
export enum EmailMessageType {
  /** 收信 */
  Received = 0,
  /** 发信 */
  Sent = 1,
  /** 删除提示 */
  DeleteNotice = 2,
  /** 退订提示 */
  UnsubscribeNotice = 3,

  AUTO_REPLY_TIP = 5, // 系统自动回复提示（无效回复）
}

/** 邮件状态 - 完整的16个后端状态 */
export enum EmailStatus {
  // 生成阶段
  GenerationQueued = 'GENERATION_QUEUED', // 生成中排队
  Generating = 'GENERATING', // 生成中
  GeneratingPending = 'GENERATING_PENDING', // 生成中等待确认
  Generated = 'GENERATED', // 生成完成
  GenerationFailed = 'GENERATION_FAILED', // 生成失败

  // 规划阶段
  ScheduleQueued = 'SCHEDULE_QUEUED', // 规划等待排期
  Scheduling = 'SCHEDULING', // 规划中
  Scheduled = 'SCHEDULED', // 规划结束
  ScheduleFailed = 'SCHEDULE_FAILED', // 规划失败

  // 取消/中止
  Abort = 'ABORT', // 取消

  // 发送阶段
  Sending = 'SENDING', // 发送中
  SendFailed = 'SEND_FAILED', // 发送失败
  Sent = 'SENT', // 已发送

  // 用户交互
  Opened = 'OPENED', // 已打开
  Clicked = 'CLICKED', // 已点击
  Replied = 'REPLIED', // 已回复
}

/** 已读状态 */
export enum EmailReadStatus {
  Unread = 0,
  Read = 1,
}

// ==================== 附件相关 ====================

/** 附件类型（来自 IApiEmailAttachment） */
export interface EmailAttachment {
  /** 下载地址 */
  downloadUrl: string;
  /** 过期时间 */
  expireTime: number;
  /** 文件名 */
  fileName: string;
  /** 文件大小（字节） */
  fileSize: number;
  /** 附件类型 */
  type: number;
}

/** 邮件附件信息（来自 IApiHmailMessagePartInfo） */
export interface EmailMessagePartInfo {
  content?: Record<string, unknown>;
  contentId?: string;
  contentLength?: number;
  contentLocation?: string;
  contentOffset?: number;
  contentType?: string;
  encoding?: string;
  estimateSize?: number;
  filename?: string;
  height?: number;
  id?: Record<string, unknown>;
  inlined?: boolean;
  width?: number;
}

// ==================== 统一邮件模型 ====================

/**
 * 统一邮件模型
 * 融合列表数据（IApiCommsFlowEmailDto）和详情数据（IApiCommsEmailDetailResp）
 */
export interface UnifiedEmailModel {
  // ========== 来自列表接口（flowsListPost）的字段 ==========

  /** 账号ID */
  accId?: string;

  /** 唯一ID（主键） */
  contactEmailMsgId: string;

  /** 联系人ID */
  contactId: string;

  /** 邮件内容ID */
  emailContentId?: string;

  /** 邮件mid */
  emailMid?: string;

  /** 邮件tid */
  emailTid?: string;

  /** 邮件状态 */
  emailStatus: EmailStatus;

  /** 消息类型：0收信，1发信，2删除提示，3退订提示 */
  messageType: EmailMessageType;

  /** 邮件时间（时间戳） */
  messageTime: number;

  /** 企业账号ID */
  qyAccId?: string;

  /** 是否已读：0未读，1已读 */
  read: EmailReadStatus;

  /** 发信人 */
  sender?: string;

  /** 主题 */
  subject: string;

  /** 摘要 */
  summary: string;

  /** 系统消息提示文本（用于系统消息的展示） */
  tip: string;

  /** 是否为系统消息（当 messageType === 2 或 3 时为 true） */
  isSystem: boolean;

  /** 回复类型（已废弃，现使用 messageType === 5 判断无效回复） */
  replyType?: number;

  /** 询价任务ID（来自 mail/flow 接口，用于 getMessageIdGet 拼接跳转 URL） */
  inquiryTaskId?: string;

  // ========== 来自详情接口（writeEmailReadPost）的字段 ==========
  // 这些字段在列表时为空，展开时才加载

  /** 邮件正文内容（HTML），展开时加载 */
  content?: string;

  /** 发件人（详情） */
  from?: string;

  /** 收件人（详情） */
  to?: string;

  /** 操作日期 */
  operateDate?: string;

  /** msgId */
  msgId?: string;

  /** 附件列表（本地附件） */
  localAttachments?: EmailAttachment[];

  /** 附件列表（普通附件） */
  attachments?: EmailMessagePartInfo[];

  /** 内联附件列表 */
  inlineAttachments?: EmailMessagePartInfo[];

  // ========== 前端状态字段 ==========

  /** 内容是否已加载（数据加载标记） */
  contentLoaded?: boolean;
}

// ==================== 列表响应类型 ====================

/** 邮件列表请求参数 */
export interface EmailListParams {
  contactId?: string;
  lastId?: string;
  lastTime?: number;
  pageSize?: number;
}

/** 邮件列表响应 */
export interface EmailListResponse {
  contactEmailList: UnifiedEmailModel[];
  lastId: string;
  lastTime: number;
  totalSize: number;
}

// ==================== 详情请求参数 ====================

/** 邮件详情请求参数 */
export interface EmailDetailParams {
  accId?: string;
  contactEmailMsgId?: string;
  contactId?: string;
}

// ==================== 标记已读请求参数 ====================

/** 标记已读/未读请求参数 */
export interface EmailMarkReadParams {
  accId: string;
  contactEmailMsgId: string;
  contactId: string;
  /** 标记已读未读，0:未读，1:已读 */
  read: EmailReadStatus;
}

// ==================== 推送消息类型 ====================

/** 邮件替换推送消息内容 */
export interface EmailReplaceChangeContent {
  /** 联系人ID */
  contactId: string;
  /** 被替换邮件的 contactEmailMsgId */
  contactEmailMsgId: string;
  /** 旧邮件的 contactEmailMsgId */
  oldContactEmailMsgId: string;
  /** 新邮件的 contactEmailMsgId */
  newContactEmailMsgId: string;
}
/**
 * 邮件内容变更推送消息内容
 */
export interface EmailContentChangeContent {
  /** 邮件id */
  emailMsgId: string;
}
